import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, ArrowLeft } from 'lucide-react';

import Dropdowns from '../../components/Dropdowns';
import BranchCard from '../customer-data/components/BranchCard';
import { fetchKeyAccountBranchData } from './api/index';

import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useCustomerDataFilters } from 'contexts/CustomerDataFiltersContext';
import { useSmoothLoading } from 'lib/use-smooth-loading';

import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { BranchData } from '../customer-data/api';

const KeyAccountBranchPage = () => {
    const lastQueryRef = React.useRef<string | null>(null);
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();

    const {
        years,
        year,
        levels,
        loading,
        error,
        handleSelectChange,
        handleBatchSelect,
        handleYearChange,
        getCurrentFilters,
    } = useCustomerDataFilters();

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const customerCode = params.get('customerCode') || '';
    const customerName = params.get('customerName') || '';

    // Data state
    const [branchData, setBranchData] = React.useState<BranchData[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [dataLoading, setDataLoading] = React.useState<boolean>(false);
    const [dataError, setDataError] = React.useState<string>('');

    // UI state
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');
    const [sortBy] = React.useState<string>('total_income');
    const [sortDir] = React.useState<'asc' | 'desc'>('desc');
    const [page, setPage] = React.useState<number>(1);
    const [pageSize] = React.useState<number>(20);

    const {
        isLoading: smoothDataLoading,
        showContent: showDataContent,
    } = useSmoothLoading(dataLoading, {
        minLoadingTime: 200,
        debounceTime: 200,
    });

    const fetchData = React.useCallback(
        async (search?: string, currentPage = 1) => {
            if (!year || !customerCode) return;

            const filters = getCurrentFilters();

            const queryKey = JSON.stringify({
                year,
                filters,
                customer_code: customerCode,
                search: search?.trim() || '',
                page: currentPage,
                page_size: pageSize,
                sortBy,
                sortDir,
            });

            // Dedupe
            if (lastQueryRef.current === queryKey) return;
            lastQueryRef.current = queryKey;

            setDataLoading(true);
            setDataError('');

            try {
                const data = await fetchKeyAccountBranchData({
                    year,
                    filters,
                    customer_code: customerCode,
                    ...(search && search.trim() && { search: search.trim() }),
                    sort: {
                        by: sortBy,
                        dir: sortDir,
                    },
                    page: currentPage,
                    page_size: pageSize,
                });

                setBranchData(data.branches);
                setTotal(data.total);
            } catch (err: unknown) {
                const isForbidden = (err as any)?.status === 403;
                const message =
                    err instanceof Error
                        ? err.message
                        : isForbidden
                            ? 'You are not allowed to access this resource.'
                            : 'Failed to fetch branch data';

                setDataError(message);

                if (isForbidden) {
                    setBranchData([]);
                    setTotal(0);
                }
            } finally {
                setDataLoading(false);
            }
        },
        [year, customerCode, getCurrentFilters, sortBy, sortDir, pageSize]
    );

    // Effects 
    React.useEffect(() => {
        if (!year || !customerCode) return;
        if (levels.length === 0) return;

        setPage(1);
        fetchData(searchTerm, 1);
    }, [year, levels, customerCode, fetchData, searchTerm]);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '#' },
            { label: 'Key Accounts', href: '/reports/key-accounts' },
            { label: 'Branch/Project Data' },
        ]);
    }, [setBreadcrumbs, customerName]);

    React.useEffect(() => {
        if (!year) return;
        setBranchData([]);
        setTotal(0);
        setPage(1);
        setSearchTerm('');
        lastQueryRef.current = null;
    }, [year]);

    // Search (debounced)
    const handleSearch = React.useCallback(
        (search: string) => {
            setPage(1);
            fetchData(search, 1);
        },
        [fetchData]
    );

    const debouncedSearchRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);


    const debouncedSearch = React.useCallback(
        (search: string) => {
            if (debouncedSearchRef.current) {
                clearTimeout(debouncedSearchRef.current);
            }
            debouncedSearchRef.current = setTimeout(() => {
                handleSearch(search);
            }, 300);
        },
        [handleSearch]
    );

    React.useEffect(() => {
        return () => {
            if (debouncedSearchRef.current) {
                clearTimeout(debouncedSearchRef.current);
            }
        };
    }, []);

    // Pagination
    const handlePageChange = React.useCallback(
        (newPage: number) => {
            setPage(newPage);
            fetchData(searchTerm, newPage);
        },
        [fetchData, searchTerm]
    );

    const totalPages = Math.ceil(total / pageSize);

    const handleBack = React.useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return (
        <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBack}
                    className="flex items-center gap-2"
                >
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-xl font-semibold text-gray-900">
                    {customerName}
                </h1>
            </div>

            {/* Filters */}
            <div className="space-y-4">
                <Dropdowns
                    year={year}
                    years={years}
                    levels={levels}
                    loading={loading}
                    onSelectChange={handleSelectChange}
                    onBatchSelect={handleBatchSelect}
                    onYearChange={handleYearChange}
                />

                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                )}
            </div>

            {/* Search + Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Search for a branch..."
                        value={searchTerm}
                        onChange={(e) => {
                            const value = e.target.value;
                            setSearchTerm(value);
                            debouncedSearch(value);
                        }}
                        className="pl-10"
                    />
                </div>

                <div className="flex gap-2">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((type) => (
                        <button
                            key={type}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === type
                                    ? 'bg-black text-white'
                                    : 'bg-white border border-black hover:bg-gray-200'
                                }`}
                            onClick={() => setConversionType(type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {/* Error */}
            {dataError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{dataError}</p>
                </div>
            )}

            {/* Loading */}
            {smoothDataLoading && (
                <div className="space-y-6">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="bg-white rounded-lg border shadow-sm">
                            <div className="bg-gray-200 animate-pulse h-16 rounded-t-lg" />
                            <div className="p-6 space-y-4">
                                <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2" />
                                <div className="h-4 bg-gray-200 animate-pulse rounded w-2/3" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty */}
            {!smoothDataLoading && branchData.length === 0 && !dataError && (
                <div className="text-center py-12">
                    <p className="text-gray-500">
                        No branch data found. Try adjusting your filters or search terms.
                    </p>
                </div>
            )}

            {/* Data */}
            {!smoothDataLoading && branchData.length > 0 && showDataContent && (
                <div className="space-y-6 transition-opacity duration-300 opacity-100">
                    <div className="text-sm text-gray-600">
                        Showing {branchData.length} of {total} branches
                        {searchTerm && ` for "${searchTerm}"`}
                    </div>

                    <div className="space-y-6">
                        {branchData.map((branch) => (
                            <BranchCard
                                key={branch.ou_code}
                                branch={branch}
                                conversionType={conversionType}
                            />
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-8">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page <= 1}
                            >
                                Previous
                            </Button>

                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) pageNum = i + 1;
                                else if (page <= 3) pageNum = i + 1;
                                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                                else pageNum = page - 2 + i;

                                return (
                                    <Button
                                        key={pageNum}
                                        variant={page === pageNum ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </Button>
                                );
                            })}

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= totalPages}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default KeyAccountBranchPage;
